// src/screens/ProductScreen.jsx
import {
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetProductDetailsQuery,
  useGetProductsQuery,
} from '../../slices/productsApiSlice';
import { LinkContainer } from 'react-router-bootstrap';
import GalleryDisplay from '../../components/GalleryDisplay';
import ImageGallery from '../../components/ImageGallery';
import ShopMessage from '../../components/ShopMessage';
import SelectBuyPopup from '../../components/SelectBuyPopup';
import SelectRentalPopup from '../../components/SelectRentalPopup';
import FavoriteButton from '../../components/FavoriteButton';
import ProductpageFooter from '../../components/ProductpageFooter';
import { addToCart } from '../../slices/cartSlice';
import { COLOR, SIZE, GENDER } from '../../constants/productConstants';
import Breadcrumb from '../../components/SEO/Breadcrumb';
import { useJsonLd } from '../../context/JsonLdContext';
import {
  FRONTEND_BASE_URL,
  RENTAL_RATE_NON_ORG,
  RENTAL_RATE_ORG,
  SHIPPING_FEE,
} from '../../constants/constants';

/* ─────────────────────────────── */

const ProductScreen = () => {
  const { id: productId } = useParams();
  const { registerFragment } = useJsonLd();

  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  /* ---------- 状態 ---------- */
  const [serviceOption, setServiceOption] = useState('rent');  // 'rent' | 'buy'
  const [useSubscription, setUseSubscription] = useState(true);
  const [rentalPeriod,   setRentalPeriod]   = useState('4days');
  const [inStore,        setInStore]        = useState(true);

  /* ---------- API ---------- */
  const { data: product }                 = useGetProductDetailsQuery(productId);
  const { data: productListData = {} }    = useGetProductsQuery({});
  const allProducts = productListData.products ?? [];

  /* ---------- 関連商品 ---------- */
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts.filter(
      (p) => p.shopName === product.shopName && p.id !== product.id,
    );
  }, [allProducts, product]);

  const sameCategoryProducts = useMemo(() => {
    if (!product?.parentCategory?.id) return [];
    const targetId = product.parentCategory.id;
    return allProducts.filter(
      (p) => p.parentCategory?.id === targetId && p.id !== product.id,
    );
  }, [allProducts, product]);

  /* ---------- 認証 ---------- */
  const { userInfo } = useSelector((state) => state.auth);
  const canUseSubscription =
    userInfo &&
    userInfo.subscription &&
    userInfo.subscription.usedItems < userInfo.subscription.maxItems &&
    product?.price <= userInfo.subscription.itemPriceLimit;

  /* ---------- 料金計算 ---------- */
  const calcBasePrice = ({
    price = 0,
    serviceOption,
    useSubscription,
    canUseSubscription,
    isOrgUser,
    rentalPeriod,
  }) => {
    if (serviceOption !== 'rent') return price;           // 購入
    if (useSubscription && canUseSubscription) return 0;  // サブスク

    const table = isOrgUser ? RENTAL_RATE_ORG : RENTAL_RATE_NON_ORG;
    const rate  = table[rentalPeriod] ?? table.default;
    return Math.round(price * rate);
  };

  const basePrice = useMemo(
    () =>
      calcBasePrice({
        price: Number(product?.price) || 0,
        serviceOption,
        useSubscription,
        canUseSubscription,
        isOrgUser: !!userInfo?.organization,
        rentalPeriod,
      }),
    [
      product?.price,
      serviceOption,
      useSubscription,
      canUseSubscription,
      userInfo?.organization,
      rentalPeriod,
    ],
  );

  const shippingCost = useMemo(() => (inStore ? 0 : SHIPPING_FEE), [inStore]);
  const finalCost    = useMemo(() => basePrice + shippingCost, [basePrice, shippingCost]);

  /* ---------- カート投入 ---------- */
  const addToCartHandler = () => {
    const itemPayload = {
      ...product,
      serviceOption,
      inStore,
      useSubscription: canUseSubscription && useSubscription,
      rentalPeriod: !useSubscription ? rentalPeriod : 'unlimited',
      finalPrice: finalCost,
      shippingCost,
    };
    dispatch(addToCart(itemPayload));
    navigate('/cart');
  };

  /* ---------- 商品が売切なら購入モード固定 ---------- */
  useEffect(() => {
    if (product && product.status !== 'available') {
      setServiceOption('buy');
    }
  }, [product]);

  /* ---------- JSON‑LD ---------- */
  useEffect(() => {
    if (!product) return;
    const audienceBlock = {
      '@type': 'PeopleAudience',
      suggestedGender:
        product.gender === 2
          ? 'https://schema.org/Male'
          : product.gender === 3
          ? 'https://schema.org/Female'
          : 'Unisex',
    };
    registerFragment({
      '@type': 'Product',
      name: product.name,
      image: [product.image, ...(product.subImages || [])],
      description: product.description,
      brand: { '@type': 'Brand', name: product.shopName },
      audience: audienceBlock,
      offers: {
        '@type': 'Offer',
        url: window.location.href,
        price: product.price,
        priceCurrency: 'JPY',
        itemCondition: 'https://schema.org/UsedCondition',
        availability:
          product.status === 'available' ? 'InStock' : 'OutOfStock',
      },
    });
  }, [product, registerFragment]);

  if (!product) return <div>Loading...</div>;

    /* ---------- 商品画像整列 ---------- */
  const images = [product.image, ...(product.subImages || [])];

  /* ─────────────────── JSX ─────────────────── */
  return (
    <div className="productpage"
    style={{
        backgroundImage: `url('https://furupura-assets.s3.ap-northeast-1.amazonaws.com/samples/backgrounds/background.webp')`,
        height: '100%',
        width: '100%',
        backgroundRepeat: 'repeat',
        overflow: 'hidden',
      }}>
      <div className="productpage-breadcrumb">
        <Breadcrumb
          items={[
            { name: 'ふるぷら', url: '/' },
            { name: product.shopName, url: `/shop/${product.Shop.slug}` },
            {
              name: product.parentCategory?.name,
              url: `/category/${product.parentCategory?.slug}`,
            },
            {
              name: product.category?.name,
              url: `/category/${product.parentCategory?.slug}/${product.category?.slug}`,
            },
            { name: product.name, url: '' },
          ]}
        />
      </div>

      {/* ================= ROW ================= */}
      <div className="productpage-row">
        {/* ---- 左カラム（画像） ---- */}
        <div className="productpage-image-column">
           <ImageGallery images={images} />
        </div>

        {/* ---- 右カラム（情報） ---- */}
        <div className="productpage-info-column">
          {/* 基本情報 */}
          <p className="productpage-label">
            {product.Tags?.map((tag) => tag.name).join(' / ')}
          </p>
          <p className="productpage-name">{product.name}</p>
          <p className="productpage-shop">{product.shopName}</p>
          <div className="productpage-pricebox">
            <span className="productpage-pricebox-price">
            ¥{product.price.toLocaleString()}
            <span className="productpage-pricebox-price-note">（定価税込）</span>
            </span>
            {/* お気に入り */}
            <div className="productpage-pricebox-favorite">
              <FavoriteButton itemId={product.id} isProduct={true}
              className="productpage-pricebox-favorite-button" />
            </div>
          </div>
          <p className="productpage-shipping">
            店舗受取であれば配送{SHIPPING_FEE.toLocaleString()}円が無料
          </p>
          <p className="productpage-stock">購入可能商品</p>

         {/* 商品購入ボタン */}
          <SelectRentalPopup
            product={product} style={{marginBottom:'1.5vw'}}/>
          <SelectBuyPopup
            product={product}/>

          {/* SHOP MESSAGE */}
          <div className="productpage-shopmessage">
            <ShopMessage product={product} />
          </div>

          {/* ===== 詳細ブロック ===== 
          {useSubscription && serviceOption === 'rent' && (
            <div className="productpage-rental-details">
  
              <div className="productpage-rental-details-text">
                <div className="productpage-rental-details-text-1">
                  <p>加入済みプラン</p>
                  <p>利用可能な商品上限金額</p>
                  <p>現在のレンタル着数</p>
                </div>
                <div className="productpage-rental-details-text-2">
                  <p>{userInfo?.subscription?.name ?? '未加入'}</p>
                  <p>
                    ¥
                    {(
                      userInfo?.subscription?.itemPriceLimit ?? 0
                    ).toLocaleString()}
                  </p>
                  <p>
                    {userInfo?.subscription?.usedItems ?? 0}/
                    {userInfo?.subscription?.maxItems ?? 0}
                  </p>
                </div>
              </div>

   
              <Form.Check
                className="productpage-rental-details-text-form"
                type="checkbox"
                label={`店舗で受け取る（${product.shopArea?.name}）`}
                checked={inStore}
                onChange={(e) => setInStore(e.target.checked)}
              />

              <div className="productpage-rental-details-button">
                <Link
                  to="/subscriptions"
                  className="productpage-rental-details-button-join"
                >
                  サブスク新規加入
                </Link>
                <button
                  className="productpage-rental-details-button-cart"
                  onClick={addToCartHandler}
                  disabled={!canUseSubscription}
                >
                  サブスクで借りる
                </button>
              </div>
            </div>
          )}

          {!useSubscription && serviceOption === 'rent' && (
            <div className="productpage-rental-details">
              <div className="productpage-rental-details-leasetext">
                <div className="productpage-rental-details-leasetext-text">
                  <p className="productpage-rental-details-leasetext-text-1">
                    レンタル期間を選択
                  </p>
                  <select
                    className="productpage-rental-details-leasetext-text-form"
                    value={rentalPeriod}
                    onChange={(e) => setRentalPeriod(e.target.value)}
                  >
                    {userInfo?.organization ? (
                      <>
                        <option value="4days">3泊4日 - 35%</option>
                        <option value="2weeks">2週間 - 45%</option>
                        <option value="1month">1ヶ月 - 55%</option>
                      </>
                    ) : (
                      <>
                        <option value="4days">3泊4日 - 40%</option>
                        <option value="2weeks">2週間 - 50%</option>
                        <option value="1month">1ヶ月 - 60%</option>
                      </>
                    )}
                  </select>
                </div>

                <Form.Check
                  className="productpage-rental-details-leasetext-form"
                  type="checkbox"
                  label={`店舗で受け取る（${product.shopArea?.name}）`}
                  checked={inStore}
                  onChange={(e) => setInStore(e.target.checked)}
                />

                <p className="productpage-rental-details-leasetext-text2">
                  総額：¥{finalCost.toLocaleString()}
                  {!inStore && (
                    <span className="small text-muted">
                      （内 送料 ¥{SHIPPING_FEE.toLocaleString()}）
                    </span>
                  )}
                </p>
              </div>
              <button
                className="productpage-rental-details-button-cart"
                onClick={addToCartHandler}
              >
                カートに入れる
              </button>
            </div>
          )}*/}

          {/* 商品詳細テーブル */}
          <div className="productpage-product-detail-table">
            <div className="productpage-product-detail-table-title">
              <p>サイズ</p>
              <p>色</p>
              <p>状態</p>
              <p>性別</p>
              <p>素材</p>
              <p>発送日</p>
            </div>
            <div className="productpage-product-detail-table-value">
              <p>{SIZE[product.size] ?? 'サイズ不明'}</p>
              <p>{COLOR[product.color] ?? 'カラー不明'}</p>
              <p>{product.status}</p>
              <p>{GENDER[product.gender] ?? '不明'}</p>
              <p>{product.material}</p>
              <p>ご注文から3営業日以内</p>
            </div>
          </div>
        </div>
        {/* 右カラムここまで */}
      </div>
      {/* ROW ここまで */}

      {/* ======== 関連商品 ======== */}
      {relatedProducts.length > 0 && (
        <section className="productpage-relateditem">
          <p className="productpage-relateditem-subtitle">
            {product.shopName}の商品
          </p>
          <h2 className="productpage-relateditem-title">Other&nbsp;Item</h2>
          <GalleryDisplay type="shop" products={relatedProducts} />
        </section>
      )}

      {sameCategoryProducts.length > 0 && (
        <section className="productpage-relateditem">
          <p className="productpage-relateitem-subtitle">同じカテゴリーの商品</p>
          <h2 className="productpage-relateditem-title">
            新着{product.parentCategory?.name}
          </h2>
          <GalleryDisplay type="shop" products={sameCategoryProducts} />
        </section>
      )}
      <ProductpageFooter product={product} />
    </div>
  );
};

export default ProductScreen;
